import { sqCursorStore, sqTrendStore } from '@/core/core.stores';
import type { BasicCursor, CursorPoint } from '@/utilities/cursor.constants';

/**
 * Adds a new cursor. If an existing cursor exists with the xValue and isCapsuleTime, it is replaced.
 *
 * @param xValue - x-value of the new cursor.
 * @param points - Contains all points for this cursor
 * @param points[key] - ID of the series with points
 * @param points[key][].yValue - y-value for the series with the specified ID at the specified x-value
 * @param isCapsuleTime - True if this is a capsule-time cursor, false for calendar-time
 */
export function addCursor(xValue: number, points: Record<string, CursorPoint[]>, isCapsuleTime: boolean) {
  sqCursorStore.actions.addCursor({
    xValue,
    capsuleTime: isCapsuleTime,
    points,
  });
}

/**
 * Toggles the selected property on the cursor object.
 *
 * @param cursor - Object representing a cursor.
 * @param capsuleTime - True if this is a capsule-time cursor, false for calendar-time
 * @param multiSelect - True if multi-selection (by holding CTRL key)
 */
export function toggleCursorSelection(cursor: BasicCursor, capsuleTime: boolean, multiSelect: boolean) {
  sqCursorStore.actions.toggleCursorSelection({
    ...cursor,
    selected: !cursor.selected,
    capsuleTime,
    multiSelect,
  });
}

/**
 * Deletes a cursor with the specified parameters.
 *
 * @param xValue - x-value for the cursor to delete
 * @param isCapsuleTime - True if this is a capsule-time cursor, false for calendar-time
 */
export function deleteCursor(xValue: number, isCapsuleTime: boolean) {
  sqCursorStore.actions.deleteCursor({
    xValue,
    capsuleTime: isCapsuleTime,
  });
}

/**
 * Toggle the cursor's 'anchored' side. Can be left or right.
 */
export function toggleAnchorFromLeft(xValue: number, anchorFromLeft: boolean, isCapsuleTime: boolean) {
  sqCursorStore.actions.toggleAnchorLeft({
    xValue,
    anchorFromLeft,
    capsuleTime: isCapsuleTime,
  });
}

/**
 * Renames a cursor with the specified xValue.
 */
export function renameCursor(xValue: number, isCapsuleTime: boolean, name: string) {
  sqCursorStore.actions.renameCursor({
    xValue,
    name,
    capsuleTime: isCapsuleTime,
  });
}

/**
 * Adjusts the anchor y-offset of a cursor with the specified xValue.
 */
export function adjustCursorAnchorYOffset(xValue: number, isCapsuleTime: boolean, yOffset: number) {
  sqCursorStore.actions.adjustAnchorYOffset({
    xValue,
    yOffset,
    capsuleTime: isCapsuleTime,
  });
}

/**
 * Deletes all cursors pf the type specified.
 *
 * @param isCapsuleTime - True to delete all capsule-time cursors, false to delete all calendar-time
 *   cursors.
 */
export function deleteAll(isCapsuleTime: boolean) {
  sqCursorStore.actions.deleteAllCursors({ capsuleTime: isCapsuleTime });
}

export function deleteSelected(isCapsuleTime: boolean) {
  sqCursorStore.actions.deleteSelectedCursors({
    capsuleTime: isCapsuleTime,
    selectedRegion: sqTrendStore.selectedRegion,
  });
}

/**
 * Sets the display of cursor y-values on/off.
 *
 * @param show - Whether or not to display cursor values
 */
export function setCursorValueFlags(show: boolean) {
  sqCursorStore.actions.setCursorValueFlags(show);
}
